import { Clock } from "lucide-react";

export type AuditLog = {
  id: string;
  action: string;
  created_at: string;
  message?: string | null;
  user_id?: string | null;
  organization_id?: string | null;
  before?: Record<string, any> | null;
  after?: Record<string, any> | null;
};

type AuditLogListProps = {
  logs: AuditLog[];
  isLoading?: boolean;
  emptyMessage?: string;
};

const getActionLabel = (action: string): string => {
  if (action === "IAM.USER_INVITE") return "Invitation envoyée";
  if (action === "IAM.USER_INVITE_ACCEPTED") return "Invitation acceptée";
  if (action === "IAM.PASSWORD_RESET_REQUESTED") return "Réinitialisation de mot de passe demandée";
  if (action === "IAM.USER_CREATE") return "Utilisateur créé";
  if (action === "IAM.USER_UPDATE") return "Utilisateur modifié";
  if (action === "IAM.USER_DELETE") return "Utilisateur supprimé";
  if (action === "IAM.PASSWORD_RESET_COMPLETED") return "Mot de passe réinitialisé";
  if (action.startsWith("IAM.")) return action.replace("IAM.", "").replace(/_/g, " ").toLowerCase();
  return action;
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString("fr-FR", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export function AuditLogList({ logs, isLoading = false, emptyMessage = "Aucun historique disponible." }: AuditLogListProps) {
  if (isLoading) {
    return (
      <div className="text-sm text-muted py-4">Chargement...</div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-sm text-muted py-4">{emptyMessage}</div>
    );
  }

  return (
    <div className="space-y-0.5">
      {logs.map((log) => (
        <div
          key={log.id}
          className="px-3 py-1.5 rounded border border-border/40 bg-surface-subtle hover:bg-surface-muted/30 transition-colors"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <Clock className="w-3.5 h-3.5 text-muted" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-xs font-medium text-text">
                  {getActionLabel(log.action)}
                </span>
                <span className="text-xs text-muted">
                  {formatDate(log.created_at)}
                </span>
              </div>
              {log.message && (
                <div className="text-xs text-muted mt-0.5">
                  {log.message}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
